import { useParams } from "@tanstack/react-router";
import { useServiceIdOverride } from "contexts/serviceIdOverride";

/**
 * The active serviceId: the route's, or a provider's override.
 *
 * The route param is the source of truth on every service route (they live
 * under `serviceLayout` at `projects/{$projectId}/services/{$serviceId}`), so
 * this is the one reader components and hooks should use. Route-owning page
 * components should prefer `getRouteApi(...).useParams()` instead: that is
 * typed against the route tree and returns a guaranteed `string`.
 *
 * Inside a `ServiceIdOverrideProvider` this returns the provider's service
 * instead. The branch Explorer uses it: on a branch route the param is the
 * parent service, and the Explorer reads the branch.
 *
 * Returns `""` on routes without a serviceId — including the env-flow and
 * create routes where `{-$serviceId}` is optional. Callers must guard on it
 * before they use it as a query variable.
 */
export const useServiceId = (): string => {
  const override = useServiceIdOverride();
  const routeServiceId = useParams({
    strict: false,
    select: (p) => p.serviceId,
  });
  return override ?? routeServiceId ?? "";
};
